import { useFindAreaComum } from "core/features/area-comum/hooks";
import { Modal } from "antd";
import { useDisclosure } from "@chakra-ui/react";
import useReserva from "core/features/reservas/hooks/useReserva";
import useSaveReserva from "core/features/reservas/hooks/useSaveReserva";
import { ReservaForm } from "core/features/reservas/typings";
import { showErrorAxios } from "infra/helpers/alerta";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import FooterModal from "ui/components/Button/FooterModal";
import FormReserva from "./FormReserva";
import ModalResumo from "../ModalResumo";
import { useLoading } from "components/GlobalLoading";

interface ModalReservaProps {
  isOpen: boolean;
  onClose: () => any;
  onSave: () => any;
  reservaId?: string;
  tenantId: string;
}

function ModalReserva({
  isOpen,
  onClose,
  reservaId,
  tenantId,
  onSave,
}: ModalReservaProps) {
  const {
    isOpen: isOpenModalResumo,
    onOpen: onOpenModalResumo,
    onClose: onCloseModalResumo,
  } = useDisclosure();

  const { mutate: saveReserva, isLoading: isLoadingSave } = useSaveReserva();
  const { data: reserva, isLoading: isLoadingId } = useReserva(reservaId);
  const { control, handleSubmit, reset, watch, setValue } =
    useForm<ReservaForm>({
      reValidateMode: "onChange",
      mode: "all",
    });
  const [currentStep, setCurrentStep] = useState(0);

  const form = watch();

  const { data: areaComum, isFetching: isFetchingAreaComum } = useFindAreaComum(
    tenantId,
    form.areaComumId
  );

  const onSubmit = async (data: ReservaForm) => {
    saveReserva(data, {
      onSuccess: () => {
        onCloseModalResumo();
        onSave();
      },
      onError: (e) => {
        showErrorAxios(e);
      },
    });
  };

  useEffect(() => {
    if (reserva) {
      reset(reserva);
    }
  }, [reserva, reset]);

  const isInvalidStep = () => {
    if (currentStep === 0) {
      if (!form.areaComumId) {
        return true;
      }
    }

    if (currentStep === 1) {
      if (
        !form.responsavelId ||
        !form.unidadeId ||
        !form.nome ||
        !form.aceitoTermos
      ) {
        return true;
      }
    }

    if (currentStep === 2) {
      if (!form.data || !form.horaInicio) {
        return true;
      }
    }

    return false;
  };

  const maxStep = 2;

  const handleSave = () => handleSubmit(onSubmit)();

  const onNext = useCallback(() => {
    if (currentStep === maxStep) {
      onOpenModalResumo();
    } else {
      setCurrentStep(Math.min(currentStep + 1, maxStep));
    }
  }, [currentStep, onOpenModalResumo]);

  const onPrev = () => {
    if (currentStep === 0) {
      onClose();
    } else {
      setCurrentStep(Math.max(currentStep - 1, 0));
    }
  };

  useLoading(isLoadingId);

  return (
    <Modal
      centered
      visible={isOpen}
      footer={
        <FooterModal
          isDisabled={isInvalidStep()}
          onSubmit={() => {
            onNext();
          }}
          onClose={() => {
            onPrev();
          }}
          showRegisterAnother={false}
          isLoadingSave={isLoadingSave}
          primaryButtonText={
            currentStep < maxStep ? "Avançar" : "Registrar evento"
          }
          buttonCancelarText={currentStep === 0 ? "Fechar" : "Voltar"}
        />
      }
      title={null}
      destroyOnClose
      onCancel={onClose}
      closable={true}
      width="800px"
    >
      <FormReserva
        control={control}
        setValue={setValue}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        form={form}
        areaComum={areaComum}
        isFetchingAreaComum={isFetchingAreaComum}
        onNext={onNext}
      />

      <ModalResumo
        isOpen={isOpenModalResumo}
        onClose={onCloseModalResumo}
        onSave={handleSave}
        isLoadingSave={isLoadingSave}
        form={form}
        areaComum={areaComum}
      />
    </Modal>
  );
}

export default ModalReserva;
